import { differenceInMinutes, format } from 'date-fns';
import { RRule, rrulestr } from 'rrule';

import type {
  AvailabilityRule,
  BlockedTimeDraft,
  BlockedTimeOccurrence,
  CreateAvailabilityRuleRequest,
  ScheduleBlockedTimeOccurrenceResponse,
  UpdateAvailabilityRuleRequest,
} from '../types/blockedTime';

export const UNAVAILABLE_TITLE = 'Unavailable';
export const ORG_WIDE_BLOCK_RESOURCE_ID = '__org_wide_block__';

const MINUTES_PER_DAY = 24 * 60;

const getZonedParts = (date: Date, timezone: string): Record<string, number> =>
  Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    })
      .formatToParts(date)
      .filter(part => part.type !== 'literal')
      // Some ICU builds render midnight under hourCycle 'h23' as '24' instead of
      // '00'; normalize that to 0. Scope the fix strictly to the hour part — a
      // blanket '24' -> '0' remap also corrupts a day-of-month (or minute/second)
      // of 24, which silently zeroed out the 24th of the month and produced
      // Invalid Date / multi-week duration corruption in all-day blocks (GDN-2224).
      .map(part => [
        part.type,
        Number(part.type === 'hour' && part.value === '24' ? '0' : part.value),
      ]),
  );

const formatDateParts = ({ year, month, day }: Record<string, number>): string =>
  `${year.toString().padStart(4, '0')}-${month.toString().padStart(2, '0')}-${day
    .toString()
    .padStart(2, '0')}`;

const formatTimeParts = ({ hour, minute }: Record<string, number>): string =>
  `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;

const getTimezoneOffsetMs = (date: Date, timezone: string): number => {
  const parts = getZonedParts(date, timezone);
  const asUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
  );

  return asUtc - date.getTime();
};

const parseDateInput = (date: string): { year: number; month: number; day: number } => {
  const [year, month, day] = date.split('-').map(Number);
  return { year, month, day };
};

const parseTimeInput = (time: string): { hour: number; minute: number } => {
  const [hour, minute] = time.split(':').map(Number);
  return { hour, minute };
};

const addDaysToDateInput = (date: string, days: number): string => {
  const { year, month, day } = parseDateInput(date);
  const utcDate = new Date(Date.UTC(year, month - 1, day + days, 0, 0, 0, 0));

  return formatDateParts({
    year: utcDate.getUTCFullYear(),
    month: utcDate.getUTCMonth() + 1,
    day: utcDate.getUTCDate(),
  });
};

const zonedDateTimeToDate = (date: string, time: string, timezone: string): Date => {
  const { year, month, day } = parseDateInput(date);
  const { hour, minute } = parseTimeInput(time);
  const utcGuess = new Date(Date.UTC(year, month - 1, day, hour, minute, 0, 0));
  const firstOffset = getTimezoneOffsetMs(utcGuess, timezone);
  const firstResult = new Date(utcGuess.getTime() - firstOffset);
  const secondOffset = getTimezoneOffsetMs(firstResult, timezone);

  return secondOffset === firstOffset ? firstResult : new Date(utcGuess.getTime() - secondOffset);
};

export const toZonedDateInputValue = (date: Date, timezone: string): string =>
  formatDateParts(getZonedParts(date, timezone));

export const toZonedTimeInputValue = (date: Date, timezone: string): string =>
  formatTimeParts(getZonedParts(date, timezone));

export const getBrowserTimeZone = (): string =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

export const toDateInputValue = (date: Date, timezone?: string): string =>
  timezone ? toZonedDateInputValue(date, timezone) : format(date, 'yyyy-MM-dd');

export const toTimeInputValue = (date: Date, timezone?: string): string =>
  timezone ? toZonedTimeInputValue(date, timezone) : format(date, 'HH:mm');

export const coercePositiveDurationMinutes = (start: Date, end: Date): number =>
  Math.max(1, differenceInMinutes(end, start));

export const buildBlockedTimeDraft = ({
  scope,
  providerId,
  date,
  endDate,
  startTime,
  endTime,
  allDay,
  timezone,
  rrule,
  recurrenceEndDate,
}: {
  scope: BlockedTimeDraft['scope'];
  providerId?: string | null;
  date: string;
  endDate: string;
  startTime: string;
  endTime: string;
  allDay: boolean;
  timezone?: string;
  rrule?: string | null;
  recurrenceEndDate?: string | null;
}): BlockedTimeDraft | null => {
  if (!date) return null;

  const resolvedTimezone = timezone || getBrowserTimeZone();

  if (allDay) {
    const localStart = zonedDateTimeToDate(date, '00:00', resolvedTimezone);
    const exclusiveEndDate = addDaysToDateInput(endDate || date, 1);
    const localEnd = zonedDateTimeToDate(exclusiveEndDate, '00:00', resolvedTimezone);
    if (localEnd <= localStart) return null;

    return {
      scope,
      providerId,
      start: localStart,
      end: localEnd,
      timezone: resolvedTimezone,
      allDay: true,
      rrule: rrule || null,
      endDate: recurrenceEndDate || null,
    };
  }

  if (!startTime || !endTime) return null;

  const start = zonedDateTimeToDate(date, startTime, resolvedTimezone);
  let resolvedEndDate = endDate || date;
  let end = zonedDateTimeToDate(resolvedEndDate, endTime, resolvedTimezone);
  if (end <= start && !endDate) {
    resolvedEndDate = addDaysToDateInput(date, 1);
    end = zonedDateTimeToDate(resolvedEndDate, endTime, resolvedTimezone);
  }
  if (end <= start) return null;

  return {
    scope,
    providerId,
    start,
    end,
    timezone: resolvedTimezone,
    allDay: false,
    rrule: rrule || null,
    endDate: recurrenceEndDate || null,
  };
};

export const toCreateAvailabilityRuleRequest = (
  draft: BlockedTimeDraft,
): CreateAvailabilityRuleRequest => {
  const request: CreateAvailabilityRuleRequest = {
    effect: 'block',
    encounter_type_id: null,
    start_time: draft.start.toISOString(),
    duration_minutes: coercePositiveDurationMinutes(draft.start, draft.end),
    timezone: draft.timezone,
    rrule: draft.rrule || null,
    end_date: draft.endDate || null,
  };

  if (draft.scope === 'organization') {
    request.provider_id = null;
  } else if (draft.scope === 'provider' || (draft.scope === 'self' && draft.providerId)) {
    request.provider_id = draft.providerId ?? null;
  }

  return request;
};

export const toUpdateAvailabilityRuleRequest = (
  draft: BlockedTimeDraft,
): UpdateAvailabilityRuleRequest => {
  const request = toCreateAvailabilityRuleRequest(draft);
  return {
    provider_id: request.provider_id,
    effect: request.effect,
    encounter_type_id: request.encounter_type_id,
    start_time: request.start_time,
    duration_minutes: request.duration_minutes,
    timezone: request.timezone,
    rrule: request.rrule,
    end_date: request.end_date,
  };
};

const inferBlockedTimeAllDayFromRange = ({
  start,
  end,
  durationMinutes,
  timezone,
}: {
  start: Date;
  end: Date;
  durationMinutes: number;
  timezone?: string | null;
}): boolean => {
  const resolvedTimezone = timezone || getBrowserTimeZone();
  const startParts = getZonedParts(start, resolvedTimezone);
  const endParts = getZonedParts(end, resolvedTimezone);

  return (
    startParts.hour === 0 &&
    startParts.minute === 0 &&
    startParts.second === 0 &&
    endParts.hour === 0 &&
    endParts.minute === 0 &&
    endParts.second === 0 &&
    durationMinutes >= MINUTES_PER_DAY - 120 &&
    toZonedDateInputValue(end, resolvedTimezone) > toZonedDateInputValue(start, resolvedTimezone)
  );
};

export const inferBlockedTimeAllDay = (rule: AvailabilityRule): boolean => {
  const start = new Date(rule.start_time);
  const end = new Date(start.getTime() + rule.duration_minutes * 60_000);

  return inferBlockedTimeAllDayFromRange({
    start,
    end,
    durationMinutes: rule.duration_minutes,
    timezone: rule.timezone,
  });
};

export const getBlockedTimeDateSpan = (
  rule: AvailabilityRule,
): { date: string; endDate: string } => {
  const start = new Date(rule.start_time);
  const end = new Date(start.getTime() + rule.duration_minutes * 60_000);
  const timezone = rule.timezone || getBrowserTimeZone();
  const date = toZonedDateInputValue(start, timezone);
  const exclusiveEndDate = toZonedDateInputValue(end, timezone);
  const inclusiveEnd = inferBlockedTimeAllDay(rule)
    ? addDaysToDateInput(exclusiveEndDate, -1)
    : exclusiveEndDate;

  return {
    date,
    endDate: inclusiveEnd,
  };
};

export const overlapsRange = (start: Date, end: Date, rangeStart: Date, rangeEnd: Date): boolean =>
  start < rangeEnd && end > rangeStart;

const normalizeRRule = (rrule: string): string =>
  rrule.toUpperCase().startsWith('RRULE:') ? rrule : `RRULE:${rrule}`;

const expandRuleStarts = (rule: AvailabilityRule, rangeStart: Date, rangeEnd: Date): Date[] => {
  const start = new Date(rule.start_time);
  const durationMs = rule.duration_minutes * 60_000;

  if (!rule.rrule) {
    const end = new Date(start.getTime() + durationMs);
    return overlapsRange(start, end, rangeStart, rangeEnd) ? [start] : [];
  }

  try {
    const parsed = rrulestr(normalizeRRule(rule.rrule), { dtstart: start });
    if (!(parsed instanceof RRule)) return [];
    return parsed.between(new Date(rangeStart.getTime() - durationMs), rangeEnd, true);
  } catch {
    return [];
  }
};

export const expandBlockedTimeRule = ({
  rule,
  rangeStart,
  rangeEnd,
  visibleResourceIds,
}: {
  rule: AvailabilityRule;
  rangeStart: Date;
  rangeEnd: Date;
  visibleResourceIds: string[];
}): BlockedTimeOccurrence[] => {
  if (rule.effect !== 'block' || !rule.availability_rule_id) return [];

  const isOrgWide = rule.provider_id == null;
  const resourceIds = isOrgWide
    ? visibleResourceIds.length > 0
      ? visibleResourceIds
      : [ORG_WIDE_BLOCK_RESOURCE_ID]
    : [rule.provider_id as string];

  const allDay = inferBlockedTimeAllDay(rule);
  const starts = expandRuleStarts(rule, rangeStart, rangeEnd);

  return starts.flatMap(start => {
    const end = new Date(start.getTime() + rule.duration_minutes * 60_000);
    if (!overlapsRange(start, end, rangeStart, rangeEnd)) return [];

    return resourceIds.map(resourceId => ({
      id: `${rule.availability_rule_id}:${start.toISOString()}:${resourceId}`,
      ruleId: rule.availability_rule_id as string,
      providerId: rule.provider_id ?? null,
      resourceId,
      start,
      end,
      timezone: rule.timezone,
      allDay,
      recurrence: rule.rrule ?? null,
      isRecurring: Boolean(rule.rrule),
      rule,
    }));
  });
};

export const blockedTimeOccurrenceFromScheduleEvent = ({
  occurrence,
  visibleResourceIds,
}: {
  occurrence: ScheduleBlockedTimeOccurrenceResponse;
  visibleResourceIds: string[];
}): BlockedTimeOccurrence[] => {
  const rule = occurrence.rule;
  if (rule.effect !== 'block' || !rule.availability_rule_id) return [];

  const resourceIds = occurrence.resource_id
    ? [occurrence.resource_id]
    : rule.provider_id == null
      ? visibleResourceIds.length > 0
        ? visibleResourceIds
        : [ORG_WIDE_BLOCK_RESOURCE_ID]
      : [rule.provider_id];

  const start = new Date(occurrence.start_time);
  const end = new Date(occurrence.end_time);
  const allDay = inferBlockedTimeAllDayFromRange({
    start,
    end,
    durationMinutes: occurrence.duration_minutes,
    timezone: occurrence.timezone || rule.timezone,
  });

  return resourceIds.map(resourceId => ({
    id: occurrence.resource_id
      ? occurrence.occurrence_id
      : `${occurrence.occurrence_id}:${resourceId}`,
    ruleId: rule.availability_rule_id as string,
    providerId: rule.provider_id ?? null,
    resourceId,
    start,
    end,
    timezone: occurrence.timezone,
    allDay,
    recurrence: occurrence.rrule ?? null,
    isRecurring: Boolean(occurrence.rrule),
    rule,
  }));
};

export const blockedTimeOverlaps = (
  draft: Pick<BlockedTimeDraft, 'start' | 'end'>,
  intervals: Array<{ start: Date; end: Date }>,
): boolean =>
  intervals.some(interval => overlapsRange(draft.start, draft.end, interval.start, interval.end));
